/** Formateo de fechas y números, en español rioplatense. */

const LOCALE = 'es-AR';

function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return toDate(value).toLocaleDateString(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatDateLong(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return toDate(value).toLocaleDateString(LOCALE, { weekday: 'long', day: 'numeric', month: 'long' });
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return toDate(value).toLocaleString(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return toDate(value).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Tiempo relativo ("hace 3 días", "en 2 semanas").
 *
 * Para una alerta importa más "hace cuánto" que la fecha exacta: el equipo de
 * seguimiento prioriza por antigüedad, no por calendario.
 */
export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return '—';

  const target = toDate(value).getTime();
  const diffMs = target - Date.now();
  const diffMinutes = Math.round(diffMs / 60_000);
  const formatter = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['minute', 1],
    ['hour', 60],
    ['day', 60 * 24],
    ['week', 60 * 24 * 7],
    ['month', 60 * 24 * 30],
    ['year', 60 * 24 * 365],
  ];

  let chosen: [Intl.RelativeTimeFormatUnit, number] = units[0]!;
  for (const unit of units) {
    if (Math.abs(diffMinutes) >= unit[1]) chosen = unit;
  }

  return formatter.format(Math.round(diffMinutes / chosen[1]), chosen[0]);
}

export function formatNumber(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString(LOCALE, { minimumFractionDigits: 0, maximumFractionDigits: decimals });
}

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${Math.round(value * 100)}%`;
}

/** Nombre acortado para listas densas: "Ana Gómez Pérez" → "Ana G. P." */
export function shortName(fullName: string | null | undefined): string {
  if (!fullName) return '—';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!;
  return [parts[0], ...parts.slice(1).map((part) => `${part.charAt(0)}.`)].join(' ');
}

export function initials(fullName: string | null | undefined): string {
  if (!fullName) return '?';
  const parts = fullName.trim().split(/\s+/);
  return ((parts[0]?.charAt(0) ?? '') + (parts[1]?.charAt(0) ?? '')).toUpperCase() || '?';
}

/**
 * Cómo se llama en castellano cada estado de un paciente.
 *
 * Vive acá y no en la pantalla de listado porque la ficha lo necesita igual, y
 * mientras estuvo en un solo lado la ficha mostraba "ACTIVE" —el nombre interno
 * del estado— en el encabezado de una historia clínica.
 */
export const PATIENT_STATUS_LABELS: Record<string, string> = {
  INVITED: 'Invitado',
  ACTIVE: 'Activo',
  PAUSED: 'Pausado',
  COMPLETED: 'Finalizado',
  WITHDRAWN: 'Retirado',
};

export function patientStatus(value: string | null | undefined): string {
  if (!value) return '—';
  return PATIENT_STATUS_LABELS[value] ?? value;
}
